import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { findShortcutByRange, getInitialRange, getRangeByShortcut } from "./utils";

/**
 * Logic thuần cho RangeCalendar:
 * - Quản lý draftRange, shortcut, tháng trái/phải, animation
 * - Đồng bộ từ props `value`
 */
export const useRangeCalendar = ({ value }) => {
  const initialRange = useMemo(() => getInitialRange(value), [value]);

  const [selectedShortcut, setSelectedShortcut] = useState(findShortcutByRange(initialRange) || "custom");
  const [draftRange, setDraftRange] = useState(initialRange);
  const [leftMonth, setLeftMonth] = useState((initialRange.fromDate || dayjs()).startOf("month"));
  const [calendarSlideDirection, setCalendarSlideDirection] = useState("left");

  // Đồng bộ khi giá trị ngoài thay đổi (fromDate/toDate)
  useEffect(() => {
    const nextRange = getInitialRange(value);
    setDraftRange(nextRange);
    setSelectedShortcut(findShortcutByRange(nextRange) || "custom");
    setLeftMonth((nextRange.fromDate || dayjs()).startOf("month"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ phụ thuộc field bên trong value
  }, [value?.fromDate, value?.toDate, value?.startDate, value?.endDate]);

  const rightMonth = leftMonth.add(1, "month");
  const hasRange = Boolean(draftRange.fromDate && draftRange.toDate);
  const calendarSlideAnimation = calendarSlideDirection === "left" ? "dateRangeSlideInLeft" : "dateRangeSlideInRight";

  const handleSelectShortcut = (shortcutId) => {
    if (shortcutId === "custom") {
      setSelectedShortcut("custom");
      return;
    }

    const range = getRangeByShortcut(shortcutId);
    setSelectedShortcut(shortcutId);
    setDraftRange(range);
    setLeftMonth(range.fromDate.startOf("month"));
    return range;
  };

  const handlePickDate = (pickedDate) => {
    if (!pickedDate) return null;
    const normalized = pickedDate.startOf("day");
    setSelectedShortcut("custom");

    let nextRange;
    setDraftRange((prev) => {
      if (!prev.fromDate || (prev.fromDate && prev.toDate)) {
        nextRange = { fromDate: normalized, toDate: null };
      } else if (normalized.isBefore(prev.fromDate)) {
        nextRange = { fromDate: normalized, toDate: prev.fromDate };
      } else {
        nextRange = { fromDate: prev.fromDate, toDate: normalized };
      }
      return nextRange;
    });
    return nextRange;
  };

  const handlePrevMonth = () => {
    setCalendarSlideDirection("right");
    setLeftMonth((prev) => prev.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCalendarSlideDirection("left");
    setLeftMonth((prev) => prev.add(1, "month"));
  };

  const handleLeftMonthChange = (month) => {
    const nextMonth = dayjs(month).startOf("month");
    setCalendarSlideDirection(nextMonth.isBefore(leftMonth, "month") ? "right" : "left");
    setLeftMonth(nextMonth);
  };

  const handleRightMonthChange = (month) => {
    const nextLeftMonth = dayjs(month).startOf("month").subtract(1, "month");
    setCalendarSlideDirection(nextLeftMonth.isBefore(leftMonth, "month") ? "right" : "left");
    setLeftMonth(nextLeftMonth);
  };

  const handleFromDateInputChange = (valueDate) => {
    setSelectedShortcut("custom");
    if (!valueDate || !valueDate.isValid()) {
      setDraftRange((prev) => ({ ...prev, fromDate: null }));
      return;
    }

    const normalizedFromDate = valueDate.startOf("day");
    setDraftRange((prev) => {
      if (prev.toDate && normalizedFromDate.isAfter(prev.toDate)) {
        return { fromDate: normalizedFromDate, toDate: normalizedFromDate };
      }
      return { fromDate: normalizedFromDate, toDate: prev.toDate };
    });
    setLeftMonth(normalizedFromDate.startOf("month"));
  };

  const handleToDateInputChange = (valueDate) => {
    setSelectedShortcut("custom");
    if (!valueDate || !valueDate.isValid()) {
      setDraftRange((prev) => ({ ...prev, toDate: null }));
      return;
    }

    const normalizedToDate = valueDate.startOf("day");
    setDraftRange((prev) => {
      if (prev.fromDate && normalizedToDate.isBefore(prev.fromDate)) {
        return { fromDate: normalizedToDate, toDate: prev.fromDate };
      }
      return { fromDate: prev.fromDate, toDate: normalizedToDate };
    });
  };

  return {
    selectedShortcut,
    setSelectedShortcut,
    draftRange,
    setDraftRange,
    leftMonth,
    setLeftMonth,
    rightMonth,
    hasRange,
    calendarSlideAnimation,
    handleSelectShortcut,
    handlePickDate,
    handleFromDateInputChange,
    handleToDateInputChange,
    handlePrevMonth,
    handleNextMonth,
    handleLeftMonthChange,
    handleRightMonthChange,
  };
};
